/**
 * Identity of a tool call, across both shapes the AI SDK produces.
 *
 * A tool the client declared statically arrives as `tool-<name>` — the name is
 * part of the type. A tool the *server* discovered at runtime (`dynamic: true`
 * on the wire) arrives as a single `dynamic-tool` type carrying `toolName` in a
 * field instead. Teammate Code is entirely the second kind: the sandbox runs
 * whatever tools its engine has, so `packages/code/src/stream.ts` marks every
 * one dynamic. MCP and Composio tools reach the transcript the same way.
 *
 * The transcript used to test `type.startsWith("tool-")` in five places, so a
 * dynamic tool matched none of them: not rendered by `message.tsx`, and
 * classified as invisible bookkeeping by `activity-segments.ts`, which dropped
 * it before it could even be folded into an activity group. Route every one of
 * those tests through here instead.
 */

/** The single part type every runtime-discovered tool call arrives under. */
export const DYNAMIC_TOOL_PART_TYPE = "dynamic-tool";

/** Whether a part type is a tool call at all, static or dynamic. */
export function isToolPartType(type: string): boolean {
  return type.startsWith("tool-") || type === DYNAMIC_TOOL_PART_TYPE;
}

/**
 * The tool's name, or undefined when the part is not a tool call.
 *
 * Static names are sliced off the type rather than split on `-`, which is what
 * the call site in `ToolCallMessage` used to do: a hyphenated MCP tool
 * (`tool-my-mcp-server`) split to `my` and then matched no config.
 */
export function toolNameOfPart(part: { type: string; toolName?: string }): string | undefined {
  if (part.type === DYNAMIC_TOOL_PART_TYPE) return part.toolName;
  if (part.type.startsWith("tool-")) return part.type.slice("tool-".length);
  return undefined;
}
